import { Component, ComponentRef, ElementRef, computed, effect, inject, signal, ViewChild, ViewContainerRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { map } from 'rxjs';

import BalloonEditor from '@ckeditor/ckeditor5-build-balloon-block';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
// PrimeNG
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SplitterModule } from 'primeng/splitter';
import { TooltipModule } from 'primeng/tooltip';
// Dataclouder
import { AspectType, CropperComponentModal, ResolutionType, StorageImageSettings } from '@dataclouder/ngx-cloud-storage';
import { LoadingBarService, TOAST_ALERTS_TOKEN, ToastAlertsAbstractService, PromptService } from '@dataclouder/ngx-core';

import { DCLessonRendererComponent } from '../dc-lesson-renderer/dc-lesson-renderer.component';
import { DialogModule } from 'primeng/dialog';

// Added DynamicContentComponent to the import below
import {
  LessonComponentEnum,
  ILesson,
  LessonComponentInterface,
  LESSONS_TOKEN,
  LessonComponentConfiguration,
} from '../../lesson-mini-components/components/lessons.clases';

import { LessonNotionService } from '../../../services/lesson-notion.service';
import { LessonUtilsService } from '../../../services/lesson-utils.service'; // Import the new utils service
import { DCLessonComponentAdderComponent } from '../dc-lesson-component-adder/dc-lesson-component-adder.component'; // Import the component adder
import { DCLessonMetadataEditorComponent } from '../dc-lesson-metadata-editor/dc-lesson-metadata-editor.component'; // Import the metadata editor
import { ComponentBuildData } from '../../lesson-mini-components/components/ComponentBuilder';

// import { getImageSuggestion, getPromptGenerateLesson } from '../../../models/prompts';
import { ChatRoleVertex, NgxVertexService } from '@dataclouder/ngx-vertex';
import { DefaultLessonsService } from '../../../services/default-lessons.service';
import { DynamicComponentsBuilderService } from '../../dynamic-components/dynamic-components-builder.service';
@Component({
  selector: 'dc-lesson-editor',
  templateUrl: './dc-lesson-editor.component.html',
  styleUrls: ['./dc-lesson-editor.component.css'],
  standalone: true,
  imports: [
    ButtonModule,
    CKEditorModule,
    CropperComponentModal,
    DCLessonRendererComponent,
    FormsModule,
    InputTextModule,
    SplitterModule,
    TooltipModule,
    DCLessonComponentAdderComponent, // Add the component adder here
    DCLessonMetadataEditorComponent, // Add the metadata editor here
    DialogModule,
  ],
  providers: [LessonNotionService], // Provide the service here
})
export class DCLessonEditorComponent {
  // Services
  #activatedRoute = inject(ActivatedRoute); // Re-inject as it's needed for navigation
  #lessonNotionService = inject(LessonNotionService);
  #lessonUtilsService = inject(LessonUtilsService);
  #router = inject(Router);
  private lessonsService = inject(LESSONS_TOKEN, { optional: true }) ?? inject(DefaultLessonsService);
  #toastService = inject(TOAST_ALERTS_TOKEN);
  #loadingBarService = inject(LoadingBarService);
  private defaultLessonsService = inject(DefaultLessonsService);
  private promptService = inject(PromptService);
  private ngxVertexService = inject(NgxVertexService);
  private cdr = inject(ChangeDetectorRef);
  private dynamicComponentsBuilderService = inject(DynamicComponentsBuilderService);
  // ViewChild
  @ViewChild('target', { read: ViewContainerRef }) target: ViewContainerRef;
  @ViewChild('dhtml', { static: true }) dhtml: ElementRef;
  // Signals States
  readonly lessonId = toSignal(inject(ActivatedRoute).paramMap.pipe(map((params: ParamMap) => params.get('id'))));
  lesson = signal<ILesson | undefined>(undefined); // Initialize as undefined
  isLoadingLesson = signal(false);
  // Computed Signals
  coverImageUrl = computed(() => {
    // Priority Order 1 Metadata Banner, 2 Banner, 3 Media First Images, 4 Default Banner TODO: reveme banner after migration to Content
    const currentLesson = this.lesson();
    if (currentLesson?.metadata?.banner?.url) {
      return currentLesson.metadata.banner.url;
    } else if (currentLesson?.banner?.url) {
      return currentLesson.banner.url;
    } else if (currentLesson?.media?.images?.find((img: any) => img.type === 'cover')) {
      // 3 Media First Images
      return currentLesson.media.images.find((img: any) => img.type === 'cover')?.url;
    }
    return '/assets/images/default_banner.webp';
  });

  // Computed signal to get dynamic components as an array for easier iteration in the template
  readonly dynamicComponentsArray = computed(() => {
    const currentLesson = this.lesson();
    if (currentLesson?.dynamicComponents) {
      return Object.values(currentLesson.dynamicComponents);
    }
    return []; // Return empty array if no lesson or no dynamic components
  });

  // States
  public components: { [key: string]: ComponentRef<LessonComponentInterface> } = {}; // Current Dynamic components
  public editor = BalloonEditor;
  public lessonComponentEnum = LessonComponentEnum;

  public coverStorageSettings: StorageImageSettings = {
    path: `lessons/${this.lessonId()}/covers`,
    fileName: 'cover',
    cropSettings: { resizeToWidth: 850, aspectRatio: AspectType.RectangleLarge, resolutions: [ResolutionType.Medium] },
  };

  constructor() {
    // Effect to fetch lesson data when ID changes
    effect(async () => {
      const id = this.lessonId();
      console.log('Lesson ID Signal:', id);
      if (id) {
        this.isLoadingLesson.set(true); // Start loading
        try {
          const fetchedLesson = await this.lessonsService.getLesson(id);
          if (fetchedLesson) {
            this.lesson.set(fetchedLesson);
          } else {
            this.lesson.set(undefined); // Reset if not found
            this.#toastService.warn({ title: 'No se encontró la lección', subtitle: 'Quizá el id es incorrecto' });
            // Optional: Navigate away or show a specific "not found" state
            // this.#router.navigate(['/path/to/lessons']);
          }
        } catch (error) {
          console.error('Error fetching lesson:', error);
          this.lesson.set(undefined); // Reset on error
          this.#toastService.error({ title: 'Error al cargar la lección', subtitle: 'Intenta de nuevo más tarde' });
        } finally {
          this.isLoadingLesson.set(false); // Stop loading
        }
      } else {
        // Handle case for new lesson (ID is null/undefined)
        this.lesson.set({ textCoded: `<h1>Nueva lección </h1> <p> Texto aquí</p>`, tags: [] } as ILesson); // Set default new lesson structure
        this.saveLesson();
        this.isLoadingLesson.set(false); // Ensure loading is off
      }
    });
  }

  /**
   * Updates a specific property on the lesson signal.
   * Used for ngModelChange events to simulate two-way binding with signals.
   * @param property The key of the ILesson property to update.
   * @param value The new value for the property.
   */
  public updateLessonProperty<K extends keyof ILesson>(property: K, value: ILesson[K]): void {
    console.log('Updating property:', property, value);
    this.lesson.update((currentLesson) => {
      if (!currentLesson) return undefined;
      return { ...currentLesson, [property]: value };
    });
  }

  public onTagRemove(tag: any): void {
    this.lesson.update((currentLesson) => {
      if (!currentLesson) return undefined;
      const updatedTags = currentLesson.tags.filter((text: string) => text !== tag.text);
      return { ...currentLesson, tags: updatedTags };
    });
  }

  public onTagAdd(tag: { value: string; input: any }): void {
    if (tag.value) {
      this.lesson.update((currentLesson) => {
        if (!currentLesson) return undefined;
        // Avoid duplicate tags if necessary
        if (currentLesson.tags.includes(tag.value)) {
          return currentLesson;
        }
        const updatedTags = [...currentLesson.tags, tag.value];
        return { ...currentLesson, tags: updatedTags };
      });
    }
    tag.input.nativeElement.value = ''; // Clear input
  }

  public async saveLesson(event?: Event): Promise<ILesson | undefined> {
    event?.preventDefault();

    const currentLesson = this.lesson();
    if (!currentLesson) {
      this.#toastService.error({ title: 'Error', subtitle: 'No hay datos de lección para guardar' });
      return undefined;
    }

    // Clean orphaned components before saving
    const lessonToSave = this.#lessonUtilsService.cleanOrphanedComponents(currentLesson);

    // TODO: Implement optimization for saving only changed data.
    // This requires comparing lessonToSave with the initially fetched state.
    this.isLoadingLesson.set(true); // Indicate saving
    try {
      // Use the cleaned lesson object for saving
      const savedLesson = await this.lessonsService.postLesson(lessonToSave);
      const currentId = this.lessonId();

      if (!currentId) {
        // No se como guardar los extras aunt

        // It was a new lesson, now it has an ID. Navigate.
        this.#toastService.success({ title: 'Se creó la lección', subtitle: 'Éxito' });
        // The effect should automatically fetch the lesson again after navigation due to paramMap change.
        this.#router.navigate(['./', savedLesson.id], { relativeTo: this.#activatedRoute });
      } else {
        // It was an existing lesson, update the signal with the potentially updated data from the backend.
        this.lesson.set(savedLesson);
        this.#toastService.info({ title: 'Se guardaron los cambios en la lección', subtitle: 'Guardado' });
        // Call the service method for validation
        this.#lessonUtilsService.validateAudios(this.lesson());
      }
      return savedLesson;
    } catch (error: any) {
      // Type error
      console.error('Error saving lesson:', error);
      this.#toastService.error({ title: 'Error al guardar', subtitle: 'No se pudieron guardar los cambios' });
      return undefined;
    } finally {
      this.isLoadingLesson.set(false); // Finish saving indication
    }
  } // Add missing closing brace for saveLesson

  // Removed openComponentBuilder method

  /**
   * Handles the event emitted when a component is added via the adder component.
   * @param result The configuration data returned from the component builder dialog. Expected format: { obj: LessonComponentConfiguration }
   */
  public onComponentAdded(result: ComponentBuildData): void {
    // Check if result and result.obj.id exist

    const newComponent = result?.obj;
    if (newComponent?.id) {
      console.log('Component builder closed, result received in editor:', newComponent);

      // Update the lesson signal, adding the transformed component to the dynamicComponents object
      this.lesson.update((currentLesson: any) => {
        if (!currentLesson) return undefined;

        // Ensure dynamicComponents object exists, initialize if not
        const currentDynamicComponents = currentLesson.dynamicComponents || {};

        // Create the updated dynamicComponents object
        const updatedDynamicComponents: Record<string, LessonComponentConfiguration<any>> = {
          ...currentDynamicComponents,
          [newComponent.id]: newComponent, // Use component's id as the key
        };

        // Return the updated lesson state
        return { ...currentLesson, dynamicComponents: updatedDynamicComponents };
      });

      // Optionally save the lesson after adding the component
      // this.saveLesson();
    }
  }

  // isLoadingLesson signal is used directly

  // Removed generateByAI and _extractTextFromEncodedJson methods.
  // This logic is now handled within DCLessonMetadataEditorComponent.

  /**
   * Handles the image upload event, updates the lesson signal via the service, and saves.
   * @param event The image upload event data.
   */
  public async onImageUploaded(event: any): Promise<void> {
    this.#lessonUtilsService.uploadCover(this.lesson, event);

    await this.saveLesson();
  }

  /**
   * Imports lesson content from Notion using the LessonNotionService.
   */
  public async importFromNotion(): Promise<void> {
    // Use the service's loading state or manage locally
    this.isLoadingLesson.set(true);
    try {
      const newContent = await this.#lessonNotionService.importAndLinkLessonFromNotion(this.lesson(), this.lessonId());

      if (newContent !== null) {
        // Update the lesson signal's textCoded property
        this.updateLessonProperty('textCoded', newContent);
        // Toast success is handled within the service now
      }
      // If newContent is null, the service handled errors/toasts
    } finally {
      // Ensure loading state is reset regardless of service outcome
      // If observing service state: this.isLoadingLesson.set(this.#lessonNotionService.isLoading());
      this.isLoadingLesson.set(false); // Keep local loading for now
    }
  }

  /**
   * Calls the LessonNotionService to improve the lesson using AI based on Notion content.
   */
  public async improveNotionWithAI(): Promise<void> {
    await this.#lessonNotionService.improveLessonWithNotionAI(this.lesson());
  }

  public showComponentDetails(data: any) {
    alert('showComponentDetails' + JSON.stringify(data));
  }

  public async generateBanner() {
    this.#toastService.info({ title: 'Generando prompt de sugerencia', subtitle: 'Por favor, espera' });

    const prompt = this.lessonsService.getPrompts().banner(this.lesson()!);

    const geminiRes = await this.ngxVertexService.generateText([{ role: ChatRoleVertex.User, content: prompt }]);

    this.promptService
      .openPrompt({
        title: 'Generar Banner',
        initialValue: geminiRes.content || '',
        message: 'Ingresa una idea de lo que quieres ver',
        acceptText: 'Generar',
        cancelText: 'Cancelar',
      })
      .then((promptResult) => {
        if (promptResult) {
          this.#loadingBarService.showIndeterminate();
          this.defaultLessonsService.generateBanner(promptResult, this.lessonId()).then((result) => {
            if (result) {
              this.updateLessonProperty('banner', (result as any).banner);
            }
            this.#loadingBarService.successAndHide();
          });
        }
      });
    // const imagePrompt = prompt('alguna idea de lo que quieres ver?');

    // this.#loadingBarService.showIndeterminate();

    // const result = await this.defaultLessonsService.generateBanner(imagePrompt, this.lessonId());
    // if (result) {
    //   this.updateLessonProperty('banner', result.banner);
    // }
    // console.log('Generated banner:', result);
    //

    // this.#loadingBarService.successAndHide();
  }

  public promptsVisible = false;
  public prompts: any;

  public showPrompts() {
    this.promptsVisible = true;
    const promptsFn = this.lessonsService.getPrompts();

    this.prompts = { banner: promptsFn.banner(this.lesson()!), content: promptsFn.content(this.lesson()!), description: promptsFn.description(this.lesson()!) };

    console.log(this.prompts);

    this.cdr.markForCheck();
  }

  public editComponent(comp: any) {
    console.log('Edit component:', comp);
    this.dynamicComponentsBuilderService.openComponentBuilder(comp.component, comp).onClose.subscribe((result) => {
      this.onComponentAdded(result);
    });
  }
}
